import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

const tocItems = [
  { id: 'intro', label: 'intro' },
  { id: 'what-is', label: 'what_is' },
  { id: 'how-to-use', label: 'how_to_use' },
  { id: 'categories', label: 'categories' },
  { id: 'shipping', label: 'shipping' },
  { id: 'qc', label: 'qc' },
  { id: 'tips', label: 'tips' },
  { id: 'comparison', label: 'comparison' },
  { id: 'safety', label: 'safety' },
  { id: 'conclusion', label: 'conclusion' },
];

const faqItems = [
  {
    question: 'O que é um CSSBuy Spreadsheet?',
    answer: 'Um CSSBuy Spreadsheet é uma ferramenta organizada que reúne produtos, categorias, links e informações relacionadas ao serviço CSSBuy. Ele ajuda consumidores a pesquisar, comparar e encontrar produtos de forma mais eficiente.',
  },
  {
    question: 'O CSSBuy Spreadsheet é oficial?',
    answer: 'Não. O CSSBuyBR.info é um recurso independente e informativo. Não somos afiliados ao CSSBuy e não representamos a empresa oficial. Nosso objetivo é organizar informações úteis para pesquisa.',
  },
  {
    question: 'Como posso encontrar produtos no CSSBuy Spreadsheet?',
    answer: 'Você pode navegar por categorias como sapatos, moletons, camisetas, jaquetas, acessórios e mais. Use a barra de pesquisa ou explore as categorias organizadas para encontrar o que procura.',
  },
  {
    question: 'O CSSBuy é seguro para usar?',
    answer: 'A segurança depende de vários fatores. Recomendamos verificar links, confirmar políticas atuais, usar métodos de pagamento seguros e sempre verificar informações através dos canais oficiais antes de tomar decisões.',
  },
  {
    question: 'Quais são os custos de envio do CSSBuy?',
    answer: 'Os custos de envio variam de acordo com peso, dimensões, destino, rota e método escolhido. Recomendamos consultar as informações atualizadas diretamente na plataforma para obter cotações precisas.',
  },
];

export default function CssbuySpreadsheetArticle() {
  const { t } = useTranslation();
  const [activeFaq, setActiveFaq] = useState<number | null>(0);

  const scrollToSection = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      {/* Article Hero */}
      <section className="relative pt-20 md:pt-24 pb-12 md:pb-16 bg-background-100">
        <div className="w-full px-4 md:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-2 mb-4">
              <a href="/" className="text-xs text-foreground-500 hover:text-primary-600 transition-colors flex items-center gap-1">
                <i className="ri-home-line w-3 h-3 flex items-center justify-center" />
                Home
              </a>
              <span className="text-foreground-300">/</span>
              <span className="text-xs text-primary-600 font-medium">CSSBuy Spreadsheet</span>
            </div>
            <h1 className="font-heading text-2xl md:text-4xl lg:text-5xl font-bold text-foreground-950 mb-4 leading-tight">
              CSSBuy Spreadsheet: Guia Completo para Encontrar e Comparar Produtos
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-xs text-foreground-500 mb-6">
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line w-3 h-3 flex items-center justify-center" />
                {t('published')}: 2026-08-15
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-refresh-line w-3 h-3 flex items-center justify-center" />
                {t('last_updated')}: 2026-08-30
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-user-line w-3 h-3 flex items-center justify-center" />
                {t('author')}: CSSBuyBR Team
              </span>
            </div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary-50 border border-primary-200 rounded-full text-xs text-primary-700">
              <i className="ri-time-line w-3 h-3 flex items-center justify-center" />
              Tempo de leitura: 8 minutos
            </div>
          </div>
        </div>
      </section>

      <div className="w-full px-4 md:px-6 lg:px-8 py-8 md:py-12">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Sidebar TOC */}
          <aside className="lg:col-span-3">
            <div className="sticky top-24 bg-white rounded-xl border border-background-200 p-5">
              <h3 className="font-semibold text-sm text-foreground-900 mb-4 flex items-center gap-2">
                <i className="ri-list-check w-4 h-4 flex items-center justify-center text-primary-500" />
                {t('table_of_contents')}
              </h3>
              <nav className="space-y-1">
                {tocItems.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => scrollToSection(item.id)}
                    className="block w-full text-left px-3 py-2 text-xs text-foreground-600 hover:text-primary-600 hover:bg-primary-50 rounded-md transition-colors"
                  >
                    {t(`article_${item.label}`)}
                  </button>
                ))}
              </nav>
            </div>
          </aside>

          {/* Main Content */}
          <article className="lg:col-span-9">
            <div className="bg-white rounded-xl border border-background-200 p-6 md:p-8 lg:p-10">
              {/* Introduction */}
              <section id="intro" className="mb-10">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
                  {t('article_intro')}
                </h2>
                <div className="prose prose-sm max-w-none text-foreground-600 leading-relaxed">
                  <p className="mb-4">
                    Encontrar produtos de qualidade através de agents de compra pode ser um desafio quando as informações estão dispersas em múltiplas fontes. O <strong>CSSBuy Spreadsheet</strong> surge como uma solução prática para organizar categorias, links e recursos relacionados ao serviço CSSBuy, permitindo que consumidores tomem decisões mais informadas e seguras.
                  </p>
                  <p>
                    Este guia completo foi desenvolvido para ajudar você a entender o que é um CSSBuy Spreadsheet, como utilizá-lo de forma eficiente, quais categorias estão disponíveis e como navegar pelas informações de envio, QC e segurança. Todo o conteúdo aqui apresentado é baseado em pesquisa independente e não representa uma afiliação oficial com a CSSBuy.
                  </p>
                </div>
              </section>

              {/* What Is */}
              <section id="what-is" className="mb-10">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
                  {t('article_what_is')}
                </h2>
                <div className="prose prose-sm max-w-none text-foreground-600 leading-relaxed">
                  <p className="mb-4">
                    O <strong>CSSBuy Spreadsheet</strong> é uma coleção organizada de produtos, links e informações categorizadas que facilitam a pesquisa de itens através do agente de compras CSSBuy. Diferente de uma simples lista de links, um spreadsheet bem estruturado oferece:
                  </p>
                  <ul className="space-y-2 mb-4 list-disc list-inside">
                    <li>Categorias claras de produtos (tênis, roupas, acessórios, etc.)</li>
                    <li>Links verificados para produtos específicos</li>
                    <li>Informações sobre QC (Quality Control)</li>
                    <li>Dados sobre custos de envio e armazenamento</li>
                    <li>Guias e recursos educativos para compradores</li>
                  </ul>
                  <p>
                    A principal vantagem de usar um CSSBuy Spreadsheet é a economia de tempo. Em vez de navegar por múltiplas páginas e fontes, você tem acesso a uma visão consolidada de produtos e informações relevantes em um único lugar.
                  </p>
                </div>
              </section>

              {/* Comparison Table */}
              <section className="mb-10">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
                  CSSBuy vs Outros Agents de Compra
                </h2>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs md:text-sm border border-background-200 rounded-lg overflow-hidden">
                    <thead className="bg-background-100">
                      <tr>
                        <th className="text-left px-4 py-3 font-semibold text-foreground-900 border-b border-background-200">Fator</th>
                        <th className="text-left px-4 py-3 font-semibold text-foreground-900 border-b border-background-200">CSSBuy</th>
                        <th className="text-left px-4 py-3 font-semibold text-foreground-900 border-b border-background-200">Superbuy</th>
                        <th className="text-left px-4 py-3 font-semibold text-foreground-900 border-b border-background-200">Sugargoo</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b border-background-200">
                        <td className="px-4 py-3 text-foreground-700 font-medium">Pesquisa de Produtos</td>
                        <td className="px-4 py-3 text-foreground-600"><i className="ri-check-line w-4 h-4 flex items-center justify-center text-primary-500 inline" /> Sim</td>
                        <td className="px-4 py-3 text-foreground-600"><i className="ri-check-line w-4 h-4 flex items-center justify-center text-primary-500 inline" /> Sim</td>
                        <td className="px-4 py-3 text-foreground-600"><i className="ri-check-line w-4 h-4 flex items-center justify-center text-primary-500 inline" /> Sim</td>
                      </tr>
                      <tr className="border-b border-background-200 bg-background-50/50">
                        <td className="px-4 py-3 text-foreground-700 font-medium">Spreadsheet Resources</td>
                        <td className="px-4 py-3 text-foreground-600"><i className="ri-check-line w-4 h-4 flex items-center justify-center text-primary-500 inline" /> Sim</td>
                        <td className="px-4 py-3 text-foreground-600"><i className="ri-subtract-line w-4 h-4 flex items-center justify-center text-foreground-400 inline" /> Limitado</td>
                        <td className="px-4 py-3 text-foreground-600"><i className="ri-check-line w-4 h-4 flex items-center justify-center text-primary-500 inline" /> Sim</td>
                      </tr>
                      <tr className="border-b border-background-200">
                        <td className="px-4 py-3 text-foreground-700 font-medium">Informação de QC</td>
                        <td className="px-4 py-3 text-foreground-600"><i className="ri-check-line w-4 h-4 flex items-center justify-center text-primary-500 inline" /> Sim</td>
                        <td className="px-4 py-3 text-foreground-600"><i className="ri-check-line w-4 h-4 flex items-center justify-center text-primary-500 inline" /> Sim</td>
                        <td className="px-4 py-3 text-foreground-600"><i className="ri-check-line w-4 h-4 flex items-center justify-center text-primary-500 inline" /> Sim</td>
                      </tr>
                      <tr className="border-b border-background-200 bg-background-50/50">
                        <td className="px-4 py-3 text-foreground-700 font-medium">Opções de Envio</td>
                        <td className="px-4 py-3 text-foreground-600"><i className="ri-check-line w-4 h-4 flex items-center justify-center text-primary-500 inline" /> Múltiplas</td>
                        <td className="px-4 py-3 text-foreground-600"><i className="ri-check-line w-4 h-4 flex items-center justify-center text-primary-500 inline" /> Múltiplas</td>
                        <td className="px-4 py-3 text-foreground-600"><i className="ri-check-line w-4 h-4 flex items-center justify-center text-primary-500 inline" /> Múltiplas</td>
                      </tr>
                      <tr>
                        <td className="px-4 py-3 text-foreground-700 font-medium">Amigável para Iniciantes</td>
                        <td className="px-4 py-3 text-foreground-600"><i className="ri-check-line w-4 h-4 flex items-center justify-center text-primary-500 inline" /> Moderado</td>
                        <td className="px-4 py-3 text-foreground-600"><i className="ri-check-line w-4 h-4 flex items-center justify-center text-primary-500 inline" /> Alto</td>
                        <td className="px-4 py-3 text-foreground-600"><i className="ri-check-line w-4 h-4 flex items-center justify-center text-primary-500 inline" /> Alto</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <p className="text-xs text-foreground-500 mt-3">
                  Nota: Informações baseadas em pesquisa independente. Verifique sempre os dados atualizados diretamente nas plataformas oficiais.
                </p>
              </section>

              {/* How to Use */}
              <section id="how-to-use" className="mb-10">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
                  {t('article_how_to_use')}
                </h2>
                <div className="prose prose-sm max-w-none text-foreground-600 leading-relaxed">
                  <p className="mb-4">
                    Utilizar um CSSBuy Spreadsheet de forma eficiente requer alguns passos simples, mas importantes para garantir que você encontre os produtos certos e evite problemas comuns.
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div className="p-4 bg-background-50 rounded-lg border border-background-200">
                      <h4 className="font-semibold text-sm text-foreground-900 mb-2">1. Defina sua Categoria</h4>
                      <p className="text-xs text-foreground-500">Comece escolhendo a categoria de produto que você procura. Isso filtra informações irrelevantes e acelera sua pesquisa.</p>
                    </div>
                    <div className="p-4 bg-background-50 rounded-lg border border-background-200">
                      <h4 className="font-semibold text-sm text-foreground-900 mb-2">2. Verifique os Links</h4>
                      <p className="text-xs text-foreground-500">Sempre confirme que os links estão funcionando e levam aos produtos corretos. Links expirados são comuns em spreadsheets.</p>
                    </div>
                    <div className="p-4 bg-background-50 rounded-lg border border-background-200">
                      <h4 className="font-semibold text-sm text-foreground-900 mb-2">3. Compare QC</h4>
                      <p className="text-xs text-foreground-500">Use as fotos de QC disponíveis para avaliar a qualidade real dos produtos antes de comprar.</p>
                    </div>
                    <div className="p-4 bg-background-50 rounded-lg border border-background-200">
                      <h4 className="font-semibold text-sm text-foreground-900 mb-2">4. Confirme Preços</h4>
                      <p className="text-xs text-foreground-500">Preços em spreadsheets podem estar desatualizados. Sempre verifique os valores atuais no site do vendedor.</p>
                    </div>
                  </div>
                </div>
              </section>

              {/* Categories */}
              <section id="categories" className="mb-10">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
                  {t('article_categories')}
                </h2>
                <div className="prose prose-sm max-w-none text-foreground-600 leading-relaxed">
                  <p className="mb-4">
                    O CSSBuy Spreadsheet organiza produtos em diversas categorias para facilitar a navegação. As principais categorias incluem:
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
                    {['Tênis', 'Moletons', 'Camisetas', 'Jaquetas', 'Calças/Shorts', 'Bonés', 'Conjuntos', 'Roupa Íntima', 'Jerseys', 'Acessórios', 'Bolsas', 'Cintos', 'Perfumes', 'Outros'].map((cat) => (
                      <div key={cat} className="flex items-center gap-2 p-3 bg-background-50 rounded-lg border border-background-200">
                        <i className="ri-check-line w-4 h-4 flex items-center justify-center text-primary-500" />
                        <span className="text-xs text-foreground-700 font-medium">{cat}</span>
                      </div>
                    ))}
                  </div>
                  <p>
                    Cada categoria contém links específicos para produtos, permitindo que você navegue diretamente para os itens de seu interesse. Lembre-se de que a disponibilidade de produtos pode mudar, então sempre verifique a data de atualização do spreadsheet.
                  </p>
                </div>
              </section>

              {/* Shipping */}
              <section id="shipping" className="mb-10">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
                  {t('article_shipping')}
                </h2>
                <div className="prose prose-sm max-w-none text-foreground-600 leading-relaxed">
                  <p className="mb-4">
                    Entender os custos de envio é crucial ao usar o CSSBuy. Os principais fatores que influenciam o custo final incluem:
                  </p>
                  <div className="overflow-x-auto mb-4">
                    <table className="w-full text-xs md:text-sm border border-background-200 rounded-lg overflow-hidden">
                      <thead className="bg-background-100">
                        <tr>
                          <th className="text-left px-4 py-3 font-semibold text-foreground-900 border-b border-background-200">Fator</th>
                          <th className="text-left px-4 py-3 font-semibold text-foreground-900 border-b border-background-200">Significado</th>
                          <th className="text-left px-4 py-3 font-semibold text-foreground-900 border-b border-background-200">O que Verificar</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-b border-background-200">
                          <td className="px-4 py-3 text-foreground-700 font-medium">Custo do Produto</td>
                          <td className="px-4 py-3 text-foreground-600">Preço do item</td>
                          <td className="px-4 py-3 text-foreground-600">Listagem atual</td>
                        </tr>
                        <tr className="border-b border-background-200 bg-background-50/50">
                          <td className="px-4 py-3 text-foreground-700 font-medium">Envio Doméstico</td>
                          <td className="px-4 py-3 text-foreground-600">Vendedor → Warehouse</td>
                          <td className="px-4 py-3 text-foreground-600">Política do vendedor</td>
                        </tr>
                        <tr className="border-b border-background-200">
                          <td className="px-4 py-3 text-foreground-700 font-medium">Envio Internacional</td>
                          <td className="px-4 py-3 text-foreground-600">Warehouse → Destino</td>
                          <td className="px-4 py-3 text-foreground-600">Rota atual</td>
                        </tr>
                        <tr className="border-b border-background-200 bg-background-50/50">
                          <td className="px-4 py-3 text-foreground-700 font-medium">Peso</td>
                          <td className="px-4 py-3 text-foreground-600">Peso da encomenda</td>
                          <td className="px-4 py-3 text-foreground-600">Medição real</td>
                        </tr>
                        <tr>
                          <td className="px-4 py-3 text-foreground-700 font-medium">Volume</td>
                          <td className="px-4 py-3 text-foreground-600">Dimensões da embalagem</td>
                          <td className="px-4 py-3 text-foreground-600">Regras volumétricas</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <p className="text-xs text-foreground-500">
                    <strong>Importante:</strong> Os custos finais de envio podem variar conforme peso, dimensões, destino, rota e preços atualizados. Sempre consulte as informações mais recentes diretamente na plataforma.
                  </p>
                </div>
              </section>

              {/* QC */}
              <section id="qc" className="mb-10">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
                  {t('article_qc')}
                </h2>
                <div className="prose prose-sm max-w-none text-foreground-600 leading-relaxed">
                  <p className="mb-4">
                    O Quality Control (QC) é uma etapa essencial no processo de compra através de agents. O CSSBuy oferece fotos de QC que permitem verificar a qualidade real do produto antes do envio internacional.
                  </p>
                  <div className="p-4 bg-accent-50 border border-accent-200 rounded-lg mb-4">
                    <h4 className="font-semibold text-sm text-accent-800 mb-2 flex items-center gap-2">
                      <i className="ri-lightbulb-line w-4 h-4 flex items-center justify-center" />
                      Dica de QC
                    </h4>
                    <p className="text-xs text-accent-700">
                      Sempre solicite fotos detalhadas de QC, incluindo medidas, tags e materiais. Isso ajuda a confirmar que o produto corresponde às expectativas antes do envio final.
                    </p>
                  </div>
                  <p>
                    As fotos de QC geralmente incluem: visão geral do produto, detalhes de costura, tags e etiquetas, medidas com fita métrica, e materiais/tecido. Use essas informações para comparar com as imagens do vendedor e avaliar se a qualidade atende suas expectativas.
                  </p>
                </div>
              </section>

              {/* Tips */}
              <section id="tips" className="mb-10">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
                  {t('article_tips')}
                </h2>
                <div className="prose prose-sm max-w-none text-foreground-600 leading-relaxed">
                  <p className="mb-4">
                    Para maximizar sua experiência com o CSSBuy Spreadsheet, considere estas dicas práticas:
                  </p>
                  <ul className="space-y-3 mb-4">
                    <li className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-primary-100 flex items-center justify-center shrink-0 mt-0.5">
                        <span className="text-xs font-bold text-primary-600">1</span>
                      </div>
                      <p className="text-sm text-foreground-600"><strong>Verifique a data de atualização</strong> — Spreadsheets desatualizados podem conter links quebrados e preços incorretos.</p>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-primary-100 flex items-center justify-center shrink-0 mt-0.5">
                        <span className="text-xs font-bold text-primary-600">2</span>
                      </div>
                      <p className="text-sm text-foreground-600"><strong>Compare múltiplas fontes</strong> — Não confie apenas em um spreadsheet. Use vários recursos para confirmar informações.</p>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-primary-100 flex items-center justify-center shrink-0 mt-0.5">
                        <span className="text-xs font-bold text-primary-600">3</span>
                      </div>
                      <p className="text-sm text-foreground-600"><strong>Entenda as políticas</strong> — Leia as políticas de reembolso, troca e envio antes de comprar.</p>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-primary-100 flex items-center justify-center shrink-0 mt-0.5">
                        <span className="text-xs font-bold text-primary-600">4</span>
                      </div>
                      <p className="text-sm text-foreground-600"><strong>Use métodos de pagamento seguros</strong> — Proteja suas transações usando métodos de pagamento com proteção ao consumidor.</p>
                    </li>
                  </ul>
                </div>
              </section>

              {/* Safety */}
              <section id="safety" className="mb-10">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
                  {t('article_safety')}
                </h2>
                <div className="prose prose-sm max-w-none text-foreground-600 leading-relaxed">
                  <p className="mb-4">
                    A segurança é uma prioridade ao usar qualquer serviço de agent de compra. Aqui estão os principais pontos a considerar:
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                      <h4 className="font-semibold text-sm text-red-800 mb-2 flex items-center gap-2">
                        <i className="ri-alert-line w-4 h-4 flex items-center justify-center" />
                        Links Falsos
                      </h4>
                      <p className="text-xs text-red-700">Verifique sempre a URL antes de clicar. Links falsos podem levar a sites de phishing.</p>
                    </div>
                    <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                      <h4 className="font-semibold text-sm text-amber-800 mb-2 flex items-center gap-2">
                        <i className="ri-lock-line w-4 h-4 flex items-center justify-center" />
                        Segurança de Pagamento
                      </h4>
                      <p className="text-xs text-amber-700">Use métodos de pagamento seguros e nunca compartilhe dados bancários por chat.</p>
                    </div>
                  </div>
                  <p>
                    Recomendamos sempre verificar informações importantes de conta, pagamento, envio e políticas através dos <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-primary-600 hover:underline">canais oficiais</a> antes de tomar qualquer ação. Este site é um recurso independente e não substitui a verificação direta com a plataforma.
                  </p>
                </div>
              </section>

              {/* Conclusion */}
              <section id="conclusion" className="mb-10">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
                  {t('article_conclusion')}
                </h2>
                <div className="prose prose-sm max-w-none text-foreground-600 leading-relaxed">
                  <p className="mb-4">
                    O <strong>CSSBuy Spreadsheet</strong> é uma ferramenta valiosa para organizar pesquisas de produtos e facilitar o processo de compra através de agents de compra. Quando usado corretamente, ele pode economizar tempo, ajudar a comparar opções e fornecer informações úteis sobre QC, envio e custos.
                  </p>
                  <p className="mb-4">
                    Lembre-se sempre de que este site é um <strong>recurso independente</strong> e não representa a CSSBuy oficial. Verifique todas as informações importantes diretamente com a plataforma antes de realizar compras ou compartilhar dados pessoais.
                  </p>
                  <div className="p-5 bg-primary-50 border border-primary-200 rounded-xl">
                    <p className="text-sm text-primary-800 font-medium mb-3">
                      Pronto para começar?
                    </p>
                    <a
                      href="https://kaspreadsheet.com"
                      target="_blank"
                      rel="noopener nofollow"
                      className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-white text-sm font-semibold rounded-lg hover:bg-primary-600 transition-colors"
                    >
                      Acessar CSSBuy Spreadsheet
                      <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center" />
                    </a>
                  </div>
                </div>
              </section>

              {/* FAQ */}
              <section className="mb-10">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-6">
                  {t('faq')}
                </h2>
                <div className="space-y-3">
                  {faqItems.map((faq, index) => (
                    <div key={index} className="border border-background-200 rounded-lg overflow-hidden">
                      <button
                        onClick={() => setActiveFaq(activeFaq === index ? null : index)}
                        className="w-full flex items-center justify-between p-4 text-left hover:bg-background-50 transition-colors"
                      >
                        <span className="font-medium text-sm text-foreground-900 pr-4">{faq.question}</span>
                        <i className={`${activeFaq === index ? 'ri-subtract-line' : 'ri-add-line'} w-5 h-5 flex items-center justify-center text-foreground-400 shrink-0`} />
                      </button>
                      {activeFaq === index && (
                        <div className="px-4 pb-4">
                          <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </section>

              {/* Internal Links */}
              <section className="mb-6">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
                  {t('related_articles')}
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <a href="/" className="flex items-center gap-3 p-4 bg-background-50 rounded-lg border border-background-200 hover:border-primary-300 hover:bg-primary-50 transition-all">
                    <i className="ri-home-line w-5 h-5 flex items-center justify-center text-primary-500" />
                    <span className="text-sm font-medium text-foreground-700">Página Inicial</span>
                  </a>
                  <a href="/cssbuy-review/" className="flex items-center gap-3 p-4 bg-background-50 rounded-lg border border-background-200 hover:border-primary-300 hover:bg-primary-50 transition-all">
                    <i className="ri-star-line w-5 h-5 flex items-center justify-center text-primary-500" />
                    <span className="text-sm font-medium text-foreground-700">CSSBuy Review</span>
                  </a>
                  <a href="/shipping/" className="flex items-center gap-3 p-4 bg-background-50 rounded-lg border border-background-200 hover:border-primary-300 hover:bg-primary-50 transition-all">
                    <i className="ri-truck-line w-5 h-5 flex items-center justify-center text-primary-500" />
                    <span className="text-sm font-medium text-foreground-700">CSSBuy Shipping Cost</span>
                  </a>
                  <a href="/safety/" className="flex items-center gap-3 p-4 bg-background-50 rounded-lg border border-background-200 hover:border-primary-300 hover:bg-primary-50 transition-all">
                    <i className="ri-shield-check-line w-5 h-5 flex items-center justify-center text-primary-500" />
                    <span className="text-sm font-medium text-foreground-700">CSSBuy Safety Guide</span>
                  </a>
                </div>
              </section>
            </div>
          </article>
        </div>
      </div>

      <Footer />
    </div>
  );
}